import { formatAnyPhoneNumber } from "@/lib/phone"
import { TwilioPhoneNumber } from "@/lib/vibrato-api"
import React, { useContext, useEffect, useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { UpgradeModalContext, UserContext, VibratoClientContext } from "./contexts"
import CreateTwilioPhoneNumberModal from "./CreateTwilioPhoneNumberModal"
import { Edit, Plus, Phone, HelpCircle, Check } from "iconoir-react"

const TwilioPhoneNumberList: React.FC = () => {
  const user = useContext(UserContext)
  const upgradeModal = useContext(UpgradeModalContext)
  const [twilioPhonenumbers, setTwilioPhoneNumbers] = useState<TwilioPhoneNumber[]>([])
  const vibratoClient = useContext(VibratoClientContext)
  const [showCreatePhoneNumberModal, setShowCreatePhoneNumberModal] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const location = useLocation()

  useEffect(() => {
    document.title = "Vibrato - Phone Numbers"
  }, [])

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    if (searchParams.get("add") === "true" && user.plan !== "freemium") {
      setShowCreatePhoneNumberModal(true)
    }
  }, [location.search, user.plan])

  useEffect(() => {
    const fetchTwilioPhoneNumbers = async () => {
      if (vibratoClient) {
        try {
          setIsLoading(true)
          const phoneNumbers = await vibratoClient.getTwilioPhoneNumbers()
          setTwilioPhoneNumbers(phoneNumbers)
        } catch (error) {
          console.error("Failed to fetch Twilio phone numbers:", error)
        } finally {
          setIsLoading(false)
        }
      }
    }

    fetchTwilioPhoneNumbers()
  }, [vibratoClient])

  const handleAddPhoneNumber = () => {
    if (user.plan === "freemium") {
      upgradeModal.setInitialView("individual")
      upgradeModal.setShowModal(true)
      upgradeModal.setHeader("Unlock Phone Numbers with Vibrato's Paid Plans!")
      upgradeModal.setDetails(
        "The phone number feature is exclusive to our premium plans. To use phone numbers, please upgrade."
      )
    } else {
      setShowCreatePhoneNumberModal(true)
    }
  }

  return (
    <div className="h-full w-full p-6 md:p-8 flex flex-col gap-6">
      <CreateTwilioPhoneNumberModal
        show={showCreatePhoneNumberModal}
        onClose={() => setShowCreatePhoneNumberModal(false)}
      />

      <div className="flex justify-between items-center flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-purple-100 rounded-xl">
            <Phone width="27" className="text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Phone Numbers</h1>
            <p className="text-gray-600">Manage your phone numbers</p>
          </div>
        </div>

        <button
          onClick={handleAddPhoneNumber}
          className="px-4 sm:px-6 py-3 bg-linear-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 min-w-[180px]"
        >
          <Plus className="w-5 h-5" />
          <span className="hidden sm:inline">Add Phone Number</span>
          <span className="sm:hidden">Add</span>
        </button>
      </div>

      <nav className="flex border-b border-gray-200 dark:border-gray-700">
        <ul className="flex flex-wrap -mb-px text-sm font-medium text-center text-gray-500 dark:text-gray-400">
          <li className="mr-2">
            <Link
              to="/phone-numbers"
              className="inline-flex items-center justify-center p-4 border-b-2 rounded-t-lg text-blue-600 border-blue-600 active dark:text-blue-500 dark:border-blue-500"
            >
              Phone Numbers
            </Link>
          </li>
          <li className="mr-2">
            <Link
              to="/callback-numbers"
              className="inline-flex items-center justify-center p-4 border-b-2 rounded-t-lg border-transparent hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300"
            >
              Callback Numbers
            </Link>
          </li>
        </ul>
      </nav>

      <div className="bg-slate-50 p-4 md:p-6 rounded-xl border border-slate-200">
        <p className="text-slate-700">
          Phone numbers allow you to make calls with a custom caller ID and receive incoming calls that can be forwarded
          to your callback number or answered with an AI assistant using a task template.
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64 bg-white rounded-2xl shadow-md">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4" />
            <p className="text-gray-600">Loading phone numbers...</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col bg-white rounded-2xl shadow-md">
          {twilioPhonenumbers.length > 0 ? (
            <div className="w-full">
              {/* Table for md and larger screens */}
              <div className="hidden md:block overflow-auto rounded-lg">
                <table className="w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Phone Number
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Configuration
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Status
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {twilioPhonenumbers.map((phoneNumber) => (
                      <tr key={phoneNumber.uuid} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-base font-medium text-gray-900">
                            {formatAnyPhoneNumber(phoneNumber.country_code, phoneNumber.phone_number)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {phoneNumber.callback_phone_number ? (
                            <div className="flex items-center">
                              <Phone className="w-5 h-5 text-green-500 mr-2" />
                              <div>
                                <div className="text-sm font-medium text-gray-900">Callback Number</div>
                                <div className="text-sm text-gray-500">
                                  {formatAnyPhoneNumber(
                                    phoneNumber.callback_phone_number.country_code,
                                    phoneNumber.callback_phone_number.phone_number
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : phoneNumber.task_template ? (
                            <div className="flex items-center">
                              <Check className="w-5 h-5 text-blue-500 mr-2" />
                              <div>
                                <div className="text-sm font-medium text-gray-900">Task Template</div>
                                <div className="text-sm text-gray-500">{phoneNumber.task_template.task_name}</div>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center">
                              <HelpCircle className="w-5 h-5 text-amber-500 mr-2" />
                              <div className="text-sm text-gray-500">Not configured</div>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Active
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right whitespace-nowrap">
                          <Link to={`/phone-numbers/${phoneNumber.uuid}`}>
                            <button className="p-2 text-indigo-600 hover:text-indigo-900 bg-indigo-50 rounded-lg transition-colors">
                              <Edit className="w-5 h-5" />
                            </button>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Card layout for mobile devices */}
              <div className="md:hidden rounded-lg overflow-hidden">
                {/* Add the table header for mobile view */}
                <div className="bg-gray-50 px-4 py-3 border-b border-t border-gray-200">
                  <div className="flex text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="w-full">Phone Number</div>
                  </div>
                </div>
                {twilioPhonenumbers.map((phoneNumber) => (
                  <div
                    key={phoneNumber.uuid}
                    className="p-4 border-b border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-500 uppercase tracking-wider mb-1">Phone Number</span>
                        <div className="text-lg font-medium text-gray-900">
                          {formatAnyPhoneNumber(phoneNumber.country_code, phoneNumber.phone_number)}
                        </div>
                      </div>
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        Active
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      {phoneNumber.callback_phone_number ? (
                        <div className="flex items-center">
                          <Phone className="w-5 h-5 text-green-500 mr-2 shrink-0" />
                          <div className="flex flex-col">
                            <div className="text-sm font-medium text-gray-900">Callback Number</div>
                            <div className="text-sm text-gray-500">
                              {formatAnyPhoneNumber(
                                phoneNumber.callback_phone_number.country_code,
                                phoneNumber.callback_phone_number.phone_number
                              )}
                            </div>
                          </div>
                        </div>
                      ) : phoneNumber.task_template ? (
                        <div className="flex items-center">
                          <Check className="w-5 h-5 text-blue-500 mr-2 shrink-0" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">Task Template</div>
                            <div className="text-sm text-gray-500">{phoneNumber.task_template.task_name}</div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <HelpCircle className="w-5 h-5 text-amber-500 mr-2 shrink-0" />
                          <div className="text-sm text-gray-500">Not configured</div>
                        </div>
                      )}
                      <Link to={`/phone-numbers/${phoneNumber.uuid}`}>
                        <button className="p-2 text-indigo-600 hover:text-indigo-900 bg-indigo-50 rounded-lg transition-colors">
                          <Edit className="w-5 h-5" />
                        </button>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-fit p-6">
              <div className="flex flex-col items-center justify-center my-4">
                <div className="p-4 bg-purple-100 rounded-full mb-4">
                  <Phone className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No phone numbers yet</h3>
                <p className="text-gray-600 text-center max-w-md mb-6">
                  Add your first phone number to make calls with a custom caller ID and receive incoming calls.
                </p>
                <button
                  onClick={handleAddPhoneNumber}
                  className="px-6 py-3 bg-linear-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Add Phone Number
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default TwilioPhoneNumberList
